/**
 * 可排序文件列表 — dnd-kit 驱动
 *
 * 核心交互（已移除拖拽排序，仅保留合并）：
 *   - 拖拽文件到另一个文件上 → 合并为组
 *   - 拖拽文件到组上 → 加入组
 */
import React, { useCallback, useRef, useState } from "react";
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Typography } from "antd";
import { FolderAddOutlined } from "@ant-design/icons";
import FileItem from "./FileItem";
import FileGroupItem from "./FileGroupItem";

const { Text } = Typography;

// 合并需要悬停的时间（ms）— 降低到 300ms，更灵敏
const MERGE_HOVER_DELAY = 300;

const SortableFileList = ({
    files,
    groups,
    order,
    onMerge,
    onAddToGroup,
    onRemoveFile,
    onRemoveFromGroup,
    onDisbandGroup,
    onRenameGroup,
    onSplitFile,
}) => {
    const [activeId, setActiveId] = useState(null);
    const [mergeTarget, setMergeTarget] = useState(null);
    const mergeTimerRef = useRef(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
    );

    const activeFile = activeId ? files.get(activeId) : null;
    const isActiveFile = !!activeFile;

    const handleDragStart = useCallback((event) => {
        setActiveId(event.active.id);
        setMergeTarget(null);
    }, []);

    const handleDragOver = useCallback((event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            clearMergeTimer();
            setMergeTarget(null);
            return;
        }

        const overId = over.id;

        // 悬停在 group drop zone 上 → 标记加入组
        if (typeof overId === "string" && overId.startsWith("group-drop-")) {
            clearMergeTimer();
            const groupId = overId.replace("group-drop-", "");
            setMergeTarget({ type: "add_to_group", groupId });
            return;
        }

        const overFile = files.get(overId);
        const activeFileObj = files.get(active.id);

        // 两个独立文件之间 → 启动合并计时
        if (activeFileObj && overFile && !activeFileObj.groupId && !overFile.groupId) {
            if (mergeTarget?.targetId !== overId) {
                clearMergeTimer();
                mergeTimerRef.current = setTimeout(() => {
                    setMergeTarget({ type: "merge", targetId: overId });
                }, MERGE_HOVER_DELAY);
            }
        } else if (overFile && overFile.groupId) {
            // 悬停在组内文件上 → 加入其所在组
            clearMergeTimer();
            setMergeTarget({ type: "add_to_group", groupId: overFile.groupId });
        } else {
            clearMergeTimer();
            setMergeTarget(null);
        }
    }, [files, mergeTarget]);

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;

        if (mergeTarget && isActiveFile) {
            if (mergeTarget.type === "merge") {
                onMerge?.(active.id, mergeTarget.targetId);
            } else if (mergeTarget.type === "add_to_group") {
                onAddToGroup?.(active.id, mergeTarget.groupId);
            }
        }
        // 不做排序 — 拖拽只用于合并

        clearMergeTimer();
        setActiveId(null);
        setMergeTarget(null);
    }, [mergeTarget, isActiveFile, onMerge, onAddToGroup]);

    const handleDragCancel = useCallback(() => {
        clearMergeTimer();
        setActiveId(null);
        setMergeTarget(null);
    }, []);

    const clearMergeTimer = () => {
        if (mergeTimerRef.current) {
            clearTimeout(mergeTimerRef.current);
            mergeTimerRef.current = null;
        }
    };

    const renderItem = (itemId) => {
        const group = groups.get(itemId);
        if (group) {
            const memberFiles = group.memberIds
                .map((fid) => files.get(fid))
                .filter(Boolean);
            const isGroupMergeTarget = mergeTarget?.type === "add_to_group" && mergeTarget.groupId === group.id;
            return (
                <div key={group.id} className={isGroupMergeTarget ? "upload-file-group-wrapper--merge-target" : ""}>
                    <FileGroupItem
                        group={group}
                        memberFiles={memberFiles}
                        onRename={onRenameGroup}
                        onDisband={onDisbandGroup}
                        onRemoveFile={onRemoveFile}
                        onRemoveFromGroup={onRemoveFromGroup}
                        onSplitFile={onSplitFile}
                    />
                </div>
            );
        }

        const file = files.get(itemId);
        if (file) {
            const isMergeTarget = mergeTarget?.type === "merge" && mergeTarget.targetId === itemId;
            return (
                <div key={file.id} className={isMergeTarget ? "upload-file-item-wrapper--merge-target" : ""}>
                    {isMergeTarget && (
                        <div className="upload-merge-indicator">
                            <FolderAddOutlined style={{ marginRight: 6 }} />
                            释放以合并为文件组
                        </div>
                    )}
                    <FileItem
                        file={file}
                        onRemove={onRemoveFile}
                        onSplit={onSplitFile}
                    />
                </div>
            );
        }

        return null;
    };

    if (order.length === 0) {
        return null;
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            <SortableContext items={order} strategy={verticalListSortingStrategy}>
                <div className="upload-sortable-list">
                    <div className="upload-sortable-list__header">
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            共 {files.size} 个文件
                            {groups.size > 0 && `，${groups.size} 个文件组`}
                            {" · "}拖拽文件到另一个文件上可合并为组
                        </Text>
                    </div>
                    {order.map(renderItem)}
                </div>
            </SortableContext>

            <DragOverlay>
                {activeId && isActiveFile && (
                    <div className="upload-drag-overlay">
                        <FileItem file={activeFile} isDragOverlay />
                    </div>
                )}
            </DragOverlay>
        </DndContext>
    );
};

export default SortableFileList;
